import React, { useState } from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { motion } from 'framer-motion';

const ContactForm = () => {
  const { t, lang } = useLang();
  const c = t.contactForm;

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    
    try {
      // Reemplaza esta URL con la URL del Webhook de producción de n8n
      const N8N_WEBHOOK_URL = 'https://n8n.ias-n8n.cloud/webhook/rdaa-contacto';
      
      const response = await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source: 'Contact Form - Website',
          language: lang,
          submittedAt: new Date().toISOString()
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      } else {
        console.error('Error enviando formulario a n8n');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error de red:', error);
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-[#f4fcf9] relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-teal-100/50 via-[#f4fcf9] to-[#f4fcf9]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-[2rem] sm:rounded-[3rem] shadow-2xl shadow-teal-900/5 overflow-hidden flex flex-col lg:flex-row border border-slate-100"
        >
          {/* Left Column - Contact Info */}
          <div className="lg:w-2/5 bg-[#1a4a4a] text-white p-8 md:p-12 lg:p-16 relative overflow-hidden flex flex-col justify-between">
            <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-emerald-500/20 rounded-full blur-2xl" />
            
            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">{c.title}</h2>
              <p className="text-teal-50 mb-12 text-lg opacity-90">
                {t.lead?.subtitle || "Fill out the short form and we'll be in touch."}
              </p>

              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/5">
                    <MapPin className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{c.campus}</h4>
                    <p className="text-teal-50/80 leading-relaxed mb-3">{c.address1}<br />{c.address2}</p>
                    <h4 className="font-bold text-lg mb-1">{c.gastoniaCampus}</h4>
                    <p className="text-teal-50/80 leading-relaxed">{c.gastoniaAddress1}<br />{c.gastoniaAddress2}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/5">
                    <Phone className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t.footer?.contact || 'Phone'}</h4>
                    <p className="text-teal-50/80">704-741-0908</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 border border-white/5">
                    <Mail className="w-6 h-6 text-teal-300" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">{t.footer?.email || 'Email'}</h4>
                    <p className="text-teal-50/80">info@royaldaacademy.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:w-3/5 p-8 md:p-12 lg:p-16">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">{c.firstName}</label>
                  <input 
                    type="text" 
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder={c.firstName}
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-800 placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">{c.lastName}</label>
                  <input 
                    type="text" 
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder={c.lastName}
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-800 placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">{c.email}</label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ejemplo@correo.com"
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-800 placeholder:text-slate-400"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 ml-1">{c.phone}</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(704) 000-0000"
                    className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-800 placeholder:text-slate-400"
                  />
                </div>
              </div>

              <div className="space-y-2 pt-2">
                <label className="text-sm font-bold text-slate-700 ml-1">{c.message}</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  required
                  placeholder={c.message}
                  className="w-full px-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none text-slate-800 placeholder:text-slate-400 resize-y"
                ></textarea>
              </div>

              <div className="pt-6">
                <button 
                  type="submit" 
                  disabled={status === 'submitting'}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-brand-primary hover:opacity-90 text-white font-bold py-4 px-10 rounded-full transition-all shadow-lg shadow-teal-900/20 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? '...' : (
                    <>
                      {c.submit}
                      <Send className="w-4 h-4 ml-1" />
                    </>
                  )}
                </button>
              </div>
              
              {status === 'success' && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100 text-emerald-800 font-medium text-sm flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-emerald-600 text-xs">✓</span>
                  </div>
                  {t.lead?.successDesc || 'Thank you! We will be in touch soon.'}
                </motion.div>
              )}

              {status === 'error' && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-800 font-medium text-sm flex items-start gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-red-600 text-xs">!</span>
                  </div>
                  {t.lead?.error || 'There was an error. Please try again or contact us directly.'}
                </motion.div>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactForm;
